import { sumAll } from 'effect/Number'
import { useTranslation } from 'react-i18next'
import { styled } from 'styled-components'

import { Lodeom } from '@/contextes/salarié'
import { Body, Grid } from '@/design-system'
import Montant from '@/pages/simulateurs/lodeom/components/Montant'

import { MobileContainer } from './MobileContainer'

type Props = {
	label: string
	data: Lodeom.MonthState[]
	codeRéduction?: string
	codeRégularisation?: string
	withRépartitionAndRégularisation?: boolean
	mobileVersion?: boolean
}

export type RémunérationBruteInput = {
	unité: string
	valeur: number
}

export default function RécapitulatifTrimestre({
	label,
	data,
	codeRéduction,
	codeRégularisation,
	withRépartitionAndRégularisation = true,
	mobileVersion = false,
}: Props) {
	const { t, i18n } = useTranslation()
	const language = i18n.language
	const displayedUnit = '€'

	const rémunération = sumAll(
		data.map((monthData) => monthData.rémunérationBrute)
	)
	const répartition = {
		IRC: sumAll(
			data.map(
				(monthData) =>
					monthData.réduction.répartition.IRC +
					monthData.régularisation.répartition.IRC
			)
		),
		Urssaf: sumAll(
			data.map(
				(monthData) =>
					monthData.réduction.répartition.Urssaf +
					monthData.régularisation.répartition.Urssaf
			)
		),
		chômage: sumAll(
			data.map(
				(monthData) =>
					monthData.réduction.répartition.chômage +
					monthData.régularisation.répartition.chômage
			)
		),
	}
	let réduction = sumAll(data.map((monthData) => monthData.réduction.value))
	let régularisation = sumAll(
		data.map((monthData) => monthData.régularisation.value)
	)
	if (réduction + régularisation > 0) {
		réduction += régularisation
		régularisation = 0
	} else {
		régularisation += réduction
		réduction = 0
	}

	const MontantRéduction = () => {
		return (
			<Montant
				id={`recap-${label.replace(/\s|\./g, '_')}-réduction`}
				rémunérationBrute={rémunération}
				réduction={réduction}
				répartition={répartition}
				displayedUnit={displayedUnit}
				language={language}
				alignment="center"
				withRépartition={withRépartitionAndRégularisation}
			/>
		)
	}

	const MontantRégularisation = () => {
		return (
			<Montant
				id={`recap-${label.replace(/\s|\./g, '_')}-régularisation`}
				rémunérationBrute={rémunération}
				réduction={régularisation}
				répartition={répartition}
				displayedUnit={displayedUnit}
				language={language}
				alignment="center"
				withRépartition={withRépartitionAndRégularisation}
			/>
		)
	}

	return mobileVersion ? (
		<MobileContainer>
			<StyledMonth>{label}</StyledMonth>
			<GridContainer container spacing={2}>
				<Grid item>
					<StyledBody>
						{t(
							'pages.simulateurs.lodeom.recap.header-réduction',
							'Réduction calculée'
						)}
						{codeRéduction && (
							<>
								<br />
								{codeRéduction}
							</>
						)}
					</StyledBody>
				</Grid>
				<Grid item>
					<StyledBody>
						<MontantRéduction />
					</StyledBody>
				</Grid>
			</GridContainer>

			{withRépartitionAndRégularisation && (
				<GridContainer container spacing={2}>
					<Grid item>
						<StyledBody>
							{t(
								'pages.simulateurs.lodeom.recap.header-régularisation',
								'Régularisation calculée'
							)}
							{codeRégularisation && (
								<>
									<br />
									{codeRégularisation}
								</>
							)}
						</StyledBody>
					</Grid>
					<Grid item>
						<StyledBody>
							<MontantRégularisation />
						</StyledBody>
					</Grid>
				</GridContainer>
			)}
		</MobileContainer>
	) : (
		<tr>
			<th scope="row">{label}</th>
			<td>
				<MontantRéduction />
			</td>
			{withRépartitionAndRégularisation && (
				<td>
					<MontantRégularisation />
				</td>
			)}
		</tr>
	)
}

const StyledMonth = styled(Body)`
	font-weight: bold;
	text-transform: capitalize;
	border-bottom: solid 1px ${({ theme }) => theme.colors.bases.primary[100]};
`
const GridContainer = styled(Grid)`
	align-items: center;
	justify-content: space-between;
`
const StyledBody = styled(Body)`
	margin-top: 0;
`
